import { Injectable, inject } from "@angular/core";
import { ConsentsDto } from "src/app/api/smart-pensjon-rest-api/models";
import { routes } from "src/app/modules/pension-plan/routes";
import { CustomerSuppliedDataLoaderService } from "src/app/services/customer-supplied-data/customer-supplied-data-loader.service";
import { NavigateToService } from "src/app/services/navigate-to.service";
import { PensionPlanProgressService } from "./pension-plan-progress.service";

/**
 * A caller that already awaited `ensureLoaded()` in the same action passes `dataLoaded: true`. The action then makes
 * one load attempt, and a load that failed or timed out is retried on the next user action.
 * It also passes the consents that `ensureLoaded()` resolved, so the popup decision does not read them again.
 */
interface NavigateOptions {
  dataLoaded?: boolean;
  readConsents?: ConsentsDto;
}

@Injectable({ providedIn: "root" })
export class NavigateToPensionPlanService {
  private readonly navigateToService = inject(NavigateToService);
  private readonly pensionPlanProgressService = inject(PensionPlanProgressService);
  private readonly customerSuppliedDataLoaderService = inject(CustomerSuppliedDataLoaderService);

  /** `readConsents` is a GET /user/consents answer read earlier in the same user action, see ensureLoaded(). */
  public navigateTo(pensionPlanRoute: string, readConsents?: ConsentsDto): void {
    const url = `${routes.Root}/${pensionPlanRoute}`;
    this.navigateWithOnboarding(url, readConsents);
  }

  /** The popup decision reuses the consents that `ensureLoaded()` read, so a cold click reads them once. */
  public navigateToProgressUrl({ dataLoaded = false, readConsents }: NavigateOptions = {}): void {
    const loaded = dataLoaded ? Promise.resolve(readConsents) : this.customerSuppliedDataLoaderService.ensureLoaded();
    void loaded.then((consents) => {
      this.pensionPlanProgressService.getProgressUrl().subscribe((progressUrl) => {
        this.navigateWithOnboarding(progressUrl, consents);
      });
    });
  }

  /** Hands the consents on only when some were read, so a navigation without them is called with the url alone. */
  private navigateWithOnboarding(url: string, readConsents: ConsentsDto | undefined): void {
    if (readConsents) {
      this.navigateToService.navigateToUrlWithOnboarding(url, readConsents);
    } else {
      this.navigateToService.navigateToUrlWithOnboarding(url);
    }
  }
}
